import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import styles from './EquipeDetalhes.module.css';
import PageHeader from '../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import GerarRelatorioPDF from './GerarRelatorioPDF';
import ReceitasModal from './ReceitasModal';
import DetalhesProducaoModal from './DetalhesProducaoModal';
import CadastroDespesaColheita from './CadastroDespesaColheita';
import ResumoDespesasEquipePopup from './ResumoDespesasEquipePopup';
import ResumoMensalPopup from './ResumoMensal';
import {
    FiUsers, FiDollarSign, FiTruck, FiUser,
    FiChevronRight, FiSearch, FiEdit, FiFileText, FiTrendingUp,
    FiClipboard, FiPieChart, FiBarChart2, FiActivity, FiPlusCircle, FiBarChart
} from 'react-icons/fi';
import Swal from 'sweetalert2';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);

const EquipeDetalhes = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [equipe, setEquipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const despesasChartRef = useRef(null);
    const receitasChartRef = useRef(null);
    const balancoChartRef = useRef(null);

    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    const [totalDespesas, setTotalDespesas] = useState(0.00);
    const [despesasPorTipo, setDespesasPorTipo] = useState([]);
    const [loadingDespesas, setLoadingDespesas] = useState(false);

    const [totalReceitas, setTotalReceitas] = useState(0.00);
    const [receitasPorData, setReceitasPorData] = useState([]);
    const [loadingReceitas, setLoadingReceitas] = useState(false);

    const [lucroLiquido, setLucroLiquido] = useState(0.00);

    const [resumoProducao, setResumoProducao] = useState({
        total_producao_m3: 0.00,
        total_horas_trabalhadas: 0.00,
        media_diaria_m3: 0.00,
        dias_com_registro: 0
    });
    const [desempenhoOperador, setDesempenhoOperador] = useState([]);
    const [loadingProducao, setLoadingProducao] = useState(false);

    const [isReceitasModalOpen, setIsReceitasModalOpen] = useState(false);
    const [isDetalhesProducaoModalOpen, setIsDetalhesProducaoModalOpen] = useState(false);
    const [isCadastroDespesaModalOpen, setIsCadastroDespesaModalOpen] = useState(false);
    const [isResumoConsolidadoOpen, setIsResumoConsolidadoOpen] = useState(false);
    const [isResumoMensalOpen, setIsResumoMensalOpen] = useState(false);

    const formatCurrency = (value) => {
        const numericValue = parseFloat(value) || 0;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(numericValue);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
        return date.toLocaleDateString('pt-BR');
    };

    const handleVeiculoDetailsClick = (veiculoId) => {
        navigate(`/frota/veiculos/${veiculoId}`);
    };

    const setDefaultDateRange = useCallback(() => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const firstDayOfMonth = `${yyyy}-${mm}-01`;
        const lastDayOfMonth = `${yyyy}-${mm}-${new Date(yyyy, today.getMonth() + 1, 0).getDate()}`;
        setDataInicio(firstDayOfMonth);
        setDataFim(lastDayOfMonth);
    }, []);

    const fetchEquipeDetails = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error("Usuário não autenticado.");

            const [response] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/colheita/equipes/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                new Promise(resolve => setTimeout(resolve, 800))
            ]);

            setEquipe(response.data);
            setError('');

            setDefaultDateRange();

        } catch (err) {
            console.error(err);
            setError("Erro ao carregar detalhes da equipe. Verifique o ID.");
        } finally {
            setLoading(false);
        }
    }, [id, setDefaultDateRange]);

    const fetchResumoProducao = useCallback(async (start, end) => {
        if (!equipe) return;
        setLoadingProducao(true);
        try {
            const token = localStorage.getItem('token');
            const params = { dataInicio: start, dataFim: end };

            const response = await axios.get(`${API_BASE_URL}/api/producao/equipes/${id}/resumo`, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });

            setResumoProducao(response.data);

        } catch (err) {
            console.error("Erro ao buscar resumo de produção:", err);
            setResumoProducao({
                total_producao_m3: 0.00,
                total_horas_trabalhadas: 0.00,
                media_diaria_m3: 0.00,
                dias_com_registro: 0
            });
        }
    }, [id, equipe]);

    const fetchDesempenhoOperador = useCallback(async (start, end) => {
        if (!equipe) return;
        setLoadingProducao(true);
        try {
            const token = localStorage.getItem('token');
            const params = { dataInicio: start, dataFim: end };

            const response = await axios.get(`${API_BASE_URL}/api/producao/equipes/${id}/desempenho-operador`, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });

            setDesempenhoOperador(response.data);

        } catch (err) {
            console.error("Erro ao buscar desempenho por operador:", err);
            setDesempenhoOperador([]);
        } finally {
            setLoadingProducao(false);
        }
    }, [id, equipe]);


    const fetchDespesas = useCallback(async (start, end) => {
    if (!equipe) return;

    setLoadingDespesas(true);
    try {
        const token = localStorage.getItem('token');
        // Chamada para o endpoint CONSOLIDADO (Equipe + Frota)
        const response = await axios.get(`${API_BASE_URL}/api/despesas-equipe/consolidado/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { dataInicio: start, dataFim: end }
        });

        const despesasConsolidadas = response.data || [];

        // Agrupamento para o Gráfico de Pizza por Categoria
        const aggregated = despesasConsolidadas.reduce((acc, exp) => {
            // Se for da Frota, usamos 'Manutenção/Combustível', se for Equipe usamos o tipo_despesa
            const type = exp.origem === 'Frota' ? 'Frota (Veículos)' : (exp.tipo_despesa || 'Outros');
            const value = parseFloat(exp.valor);
            if (!isNaN(value)) {
                acc[type] = (acc[type] || 0) + value;
            }
            return acc;
        }, {});

        const chartData = Object.keys(aggregated).map(type => ({
            type,
            value: aggregated[type]
        })).filter(d => d.value > 0);

        // O total agora somará TUDO (Equipe + Frota)
        const totalCalculated = despesasConsolidadas.reduce((sum, item) => sum + (parseFloat(item.valor) || 0), 0);

        setTotalDespesas(totalCalculated);
        setDespesasPorTipo(chartData);
        return totalCalculated;

    } catch (err) {
        console.error("Erro ao buscar despesas consolidadas:", err);
        setTotalDespesas(0.00);
        setDespesasPorTipo([]);
        return 0;
    } finally {
        setLoadingDespesas(false);
    }
}, [id, equipe]);

    const fetchReceitas = useCallback(async (start, end) => {
        if (!equipe) return;

        setLoadingReceitas(true);
        try {
            const token = localStorage.getItem('token');
            const params = { dataInicio: start, dataFim: end };

            const response = await axios.get(`${API_BASE_URL}/api/colheita/equipes/${id}/receitas`, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });

            const receitasList = response.data || [];

            const totalCalculated = receitasList.reduce((sum, item) => sum + (parseFloat(item.valor_total) || 0), 0);
            setTotalReceitas(totalCalculated);

            const aggregated = receitasList.reduce((acc, rec) => {
                const dateRaw = rec.data ? rec.data.split('T')[0] : 'N/A';
                const [, mm, dd] = dateRaw.split('-');
                const label = `${dd}/${mm}`;

                const value = parseFloat(rec.valor_total);
                if (!isNaN(value)) {
                    acc[label] = (acc[label] || 0) + value;
                }
                return acc;
            }, {});

            let chartData = Object.keys(aggregated).map(dateLabel => ({
                date: dateLabel,
                value: aggregated[dateLabel]
            }));

            chartData = chartData.reverse();

            setReceitasPorData(chartData);
            return totalCalculated;

        } catch (err) {
            console.error("Erro ao buscar receitas por período:", err);
            setTotalReceitas(0.00);
            setReceitasPorData([]);
            return 0;
        } finally {
            setLoadingReceitas(false);
        }
    }, [id, equipe]);

    const updateFinancialData = useCallback(async (start, end) => {
        if (equipe && start && end) {
            const tDespesas = await fetchDespesas(start, end);
            const tReceitas = await fetchReceitas(start, end);
            setLucroLiquido(tReceitas - tDespesas);

            fetchResumoProducao(start, end);
            fetchDesempenhoOperador(start, end);
        }
    }, [equipe, fetchDespesas, fetchReceitas, fetchResumoProducao, fetchDesempenhoOperador]);

    useEffect(() => {
        fetchEquipeDetails();
    }, [fetchEquipeDetails]);

    useEffect(() => {
        if (equipe && dataInicio && dataFim) {
            updateFinancialData(dataInicio, dataFim);
        }
    }, [equipe, dataInicio, dataFim, updateFinancialData]);

    const handleSearch = () => {
        if (dataInicio && dataFim) {
            updateFinancialData(dataInicio, dataFim);
        } else {
            Swal.fire('Atenção', 'Selecione as datas de Início e Fim para realizar a busca.', 'warning');
        }
    };

    const handleGerarRelatorio = () => {
        if (equipe && totalDespesas !== null && resumoProducao && dataInicio && dataFim) {
            const despesasImg = despesasChartRef.current ? despesasChartRef.current.toBase64Image() : null;
            const receitasImg = receitasChartRef.current ? receitasChartRef.current.toBase64Image() : null;
            const balancoImg = balancoChartRef.current ? balancoChartRef.current.toBase64Image() : null;

            GerarRelatorioPDF(
                equipe,
                totalDespesas,
                totalReceitas,
                lucroLiquido,
                resumoProducao,
                desempenhoOperador,
                dataInicio,
                dataFim,
                equipe.veiculos,
                { despesasImg, receitasImg, balancoImg }
            );
        } else {
            Swal.fire('Atenção', 'Aguarde o carregamento completo dos dados ou selecione um período de despesa válido.', 'warning');
        }
    };

    const handleOpenReceitasModal = () => setIsReceitasModalOpen(true);
    const handleCloseReceitasModal = () => setIsReceitasModalOpen(false);

    const handleOpenDetalhesProducaoModal = () => setIsDetalhesProducaoModalOpen(true);
    const handleCloseDetalhesProducaoModal = () => setIsDetalhesProducaoModalOpen(false);

    const handleOpenCadastroDespesaModal = () => setIsCadastroDespesaModalOpen(true);
    const handleCloseCadastroDespesaModal = () => {
        setIsCadastroDespesaModalOpen(false);
        updateFinancialData(dataInicio, dataFim);
    };

    const handleOpenResumoMensal = () => setIsResumoMensalOpen(true);
    const handleCloseResumoMensal = () => setIsResumoMensalOpen(false);

    const expenseColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'];
    const pieDespesasData = {
        labels: despesasPorTipo.map(d => d.type),
        datasets: [{
            data: despesasPorTipo.map(d => d.value),
            backgroundColor: expenseColors.slice(0, despesasPorTipo.length),
            borderColor: '#fff',
            borderWidth: 2,
        }],
    };

    const barReceitasData = {
        labels: receitasPorData.map(d => d.date),
        datasets: [{
            label: 'Receita (R$)',
            data: receitasPorData.map(d => d.value),
            backgroundColor: '#10b981',
            borderRadius: 4,
            barPercentage: 0.6,
        }]
    };

    const barBalanceData = {
        labels: ['Receita Total', 'Despesa Total', 'Lucro Líquido'],
        datasets: [{
            label: 'Valores (R$)',
            data: [totalReceitas, totalDespesas, lucroLiquido],
            backgroundColor: [
                '#10b981',
                '#ef4444',
                lucroLiquido >= 0 ? '#3b82f6' : '#991b1b'
            ],
            borderRadius: 6,
            barPercentage: 0.6,
        }]
    };

    const commonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            datalabels: { display: false },
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 12,
                    padding: 15,
                    font: { size: 11, weight: '600' },
                    color: '#334155'
                },
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                callbacks: {
                    label: (context) => ` ${formatCurrency(context.parsed.y || context.parsed)}`
                }
            }
        },
    };

    const barOptions = {
        ...commonChartOptions,
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: '#f1f5f9' },
                ticks: { callback: (value) => formatCurrency(value) }
            },
            x: {
                grid: { display: false }
            }
        },
        plugins: {
            ...commonChartOptions.plugins,
            legend: { display: false }
        }
    };


    if (loading) {
        return <LoadingOverlay text={`Carregando Equipe #${id}...`} />;
    }

    if (error) {
        return <div className={styles.errorMsg}>{error}</div>;
    }

    if (!equipe) {
        return <div className={styles.errorMsg}>Equipe não encontrada.</div>;
    }

    const isLoadingData = loadingDespesas || loadingProducao || loadingReceitas;


    return (
        <div className={styles.container}>
            {isLoadingData && <LoadingOverlay text="Atualizando dados..." isSmall={true} />}
            <PageHeader
                title={`Equipe: ${equipe.nome}`}
                subtitle={`Detalhes operacionais e custos mensais da equipe #${equipe.id}`}
                backTo="/lista-equipes"
            />

            <div className={styles.actionGroup}>
                <button
                    className={styles.btnEdit}
                    onClick={() => navigate(`/editar-equipe/${equipe.id}`)}
                >
                    <FiEdit size={16} style={{ marginRight: '0.5rem' }} /> Editar Equipe
                </button>
                <button
                    className={styles.btnRevenue}
                    onClick={handleOpenReceitasModal}
                    disabled={isReceitasModalOpen}
                >
                    <FiTrendingUp size={16} style={{ marginRight: '0.5rem' }} /> Ver Receitas
                </button>
                <button
                    className={styles.btnReport}
                    onClick={handleOpenDetalhesProducaoModal}
                    disabled={isLoadingData}
                >
                    <FiClipboard size={16} style={{ marginRight: '0.5rem' }} /> Ver Registros Produção
                </button>
                <button
                    className={styles.btnReport}
                    onClick={handleGerarRelatorio}
                    disabled={isLoadingData}
                >
                    <FiFileText size={16} style={{ marginRight: '0.5rem' }} /> Gerar Relatório PDF
                </button>
                <button
                    className={styles.btnReport}
                    onClick={handleOpenCadastroDespesaModal}
                    disabled={isLoadingData}
                >
                    <FiPlusCircle size={16} style={{ marginRight: '0.5rem' }} /> Despesas Frota
                </button>
                <button
                    className={styles.btnReport}
                    onClick={() => setIsResumoConsolidadoOpen(true)}
                    style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
                >
                    <FiBarChart size={16} style={{ marginRight: '0.5rem' }} /> Despesas Equipe
                </button>
                <button
                className={styles.btnReport}
                onClick={handleOpenResumoMensal}
                disabled={isLoadingData}>
                    Resumo Mensal
                </button>
            </div>

            <div className={styles.content}>

                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <FiUsers size={24} className={styles.statIcon} />
                        <span className={styles.statLabel}>Criação</span>
                        <span className={styles.statValue}>{formatDate(equipe.criado_em)}</span>
                    </div>

                    <div className={styles.statCard}>
                        <FiTruck size={24} className={styles.statIcon} />
                        <span className={styles.statLabel}>Veículos na Frota</span>
                        <span className={styles.statValue}>{equipe.veiculos.length}</span>
                    </div>

                    <div className={styles.statCardGastos}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <div>
                                <FiDollarSign size={24} className={styles.statIcon} />
                                <span className={styles.statLabelDespesa}>Lucro Líquido (Período)</span>
                                <span className={styles.statValueDespesa} style={{ color: lucroLiquido >= 0 ? '#3b82f6' : '#dc2626' }}>
                                    {formatCurrency(lucroLiquido)}
                                </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <span className={styles.statLabel} style={{ display: 'block' }}>Receita Total</span>
                                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#059669' }}>{formatCurrency(totalReceitas)}</span>
                                <span className={styles.statLabel} style={{ display: 'block', marginTop: '0.5rem' }}>Despesa Total</span>
                                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{formatCurrency(totalDespesas)}</span>
                            </div>
                        </div>

                        <div className={styles.datePickerRow}>
                            <input
                                type="date"
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                                className={styles.dateInput}
                                disabled={isLoadingData}
                            />
                            <input
                                type="date"
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                                className={styles.dateInput}
                                disabled={isLoadingData}
                            />
                            <button
                                onClick={handleSearch}
                                disabled={isLoadingData || !dataInicio || !dataFim}
                                className={styles.btnSearch}
                            >
                                <FiSearch size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                <h3 className={styles.sectionTitle} style={{ marginTop: '1rem' }}>
                    <FiActivity /> Análise Financeira
                </h3>

                <div className={styles.chartsGrid}>
                    <div className={styles.statCard}>
                        <h4 className={styles.chartTitle} style={{ color: '#ef4444' }}>
                            <FiPieChart style={{ color: '#ef4444' }} /> Despesas por Categoria
                        </h4>
                        <div className={styles.chartContainer}>
                            {despesasPorTipo.length > 0 ? (
                                <Pie ref={despesasChartRef} data={pieDespesasData} options={commonChartOptions} />
                            ) : <p className={styles.emptyChart}>Sem dados de despesa.</p>}
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <h4 className={styles.chartTitle} style={{ color: '#059669' }}>
                            <FiTrendingUp style={{ color: '#059669' }} /> Evolução de Receita (Dia)
                        </h4>
                        <div className={styles.chartContainer}>
                            {receitasPorData.length > 0 ? (
                                <Bar ref={receitasChartRef} data={barReceitasData} options={barOptions} />
                            ) : <p className={styles.emptyChart}>Sem receitas no período.</p>}
                        </div>
                    </div>

                    <div className={styles.statCard}>
                        <h4 className={styles.chartTitle} style={{ color: '#3b82f6' }}>
                            <FiBarChart2 style={{ color: '#3b82f6' }} /> Balanço Financeiro
                        </h4>
                        <div className={styles.chartContainer}>
                            <Bar ref={balancoChartRef} data={barBalanceData} options={barOptions} />
                        </div>
                    </div>
                </div>

                <h3 className={styles.sectionTitle}>
                    <FiTruck /> Maquinário e Operadores Vinculados
                </h3>

                {equipe.veiculos.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>Nenhum veículo vinculado a esta equipe.</p>
                    </div>
                ) : (
                    <div className={styles.veiculosGrid}>
                        {equipe.veiculos.map((v, index) => (
                            <div
                                key={v.id || index}
                                className={styles.veiculoCard}
                                onClick={() => handleVeiculoDetailsClick(v.id)}
                            >
                                <div className={styles.header}>
                                    <FiTruck size={20} />
                                    <h4>{v.modelo}</h4>
                                </div>

                                <div className={styles.detailItem}>
                                    <span className={styles.detailLabel}>Placa/Chassi:</span>
                                    <span className={styles.detailValue}>{v.placa_ou_chassi || 'N/A'}</span>
                                </div>
                                <div className={styles.detailItem}>
                                    <FiUser size={16} />
                                    <span className={styles.detailLabel}>Operador:</span>
                                    <span className={styles.detailValue}>{v.operador || 'N/A'}</span>
                                </div>

                                <div className={styles.linkIndicator}>
                                    Ver Detalhes <FiChevronRight size={14} style={{ marginBottom: -2 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isReceitasModalOpen && (
                <ReceitasModal
                    equipeId={equipe.id}
                    nomeEquipe={equipe.nome}
                    onClose={handleCloseReceitasModal}
                />
            )}
            {isDetalhesProducaoModalOpen && (
                <DetalhesProducaoModal
                    equipeId={equipe.id}
                    nomeEquipe={equipe.nome}
                    onClose={handleCloseDetalhesProducaoModal}
                    veiculos={equipe.veiculos}
                />
            )}
            {isCadastroDespesaModalOpen && (
                <CadastroDespesaColheita
                    equipeId={equipe.id}
                    nomeEquipe={equipe.nome}
                    veiculos={equipe.veiculos}
                    onClose={handleCloseCadastroDespesaModal}
                />
            )}
            {isResumoConsolidadoOpen && (
                <ResumoDespesasEquipePopup
                    equipeId={equipe.id}
                    nomeEquipe={equipe.nome}
                    dataInicio={dataInicio}
                    dataFim={dataFim}
                    onClose={() => setIsResumoConsolidadoOpen(false)}
                />
            )}
            {isResumoMensalOpen && (
    <ResumoMensalPopup
        equipeId={equipe.id}
        nomeEquipe={equipe.nome}
        onClose={handleCloseResumoMensal}
    />
)}
        </div>
    );
};

export default EquipeDetalhes;